import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";

// === EAGER (bundle critico iniziale) ===
// Le sole pagine che servono al primo paint pubblico (homepage / login / OAuth callback).
// Tutte le altre vengono code-splittate via React.lazy → chunk separati.
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";

// === LAZY (chunk on-demand) ===
// Service Pages pubbliche (caricate solo quando l'utente naviga sul servizio specifico).
const LuceGas = lazy(() => import("./pages/servizi/LuceGas"));
const ServiziCAF = lazy(() => import("./pages/servizi/ServiziCAF"));
const Assicurazioni = lazy(() => import("./pages/servizi/Assicurazioni"));
const Finanziamenti = lazy(() => import("./pages/servizi/Finanziamenti"));
const PropertyFinder = lazy(() => import("./pages/servizi/PropertyFinder"));

// Public-but-niche pages
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const RichiediPratica = lazy(() => import("./pages/RichiediPratica"));
const BookAppointment = lazy(() => import("./pages/BookAppointment"));

// Protected dashboards & altre rotte autenticate (chunk pesanti — caricati solo dopo login)
const ClienteDashboard = lazy(() => import("./pages/ClienteDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const DocumentUpload = lazy(() => import("./pages/DocumentUpload"));
const ImmobiliareShowcase = lazy(() => import("./pages/ImmobiliareShowcase"));
const ComparatoreBollette = lazy(() => import("./pages/ComparatoreBollette"));

/**
 * Spinner minimale, low-CLS (riserva l'altezza viewport) usato come fallback Suspense.
 * Mostrato durante il caricamento di un chunk lazy.
 */
const RouteFallback = () => (
  <div
    className="min-h-screen w-full flex items-center justify-center bg-[#020617]"
    style={{ minHeight: '100vh' }}
    role="status"
    aria-label="Caricamento"
  >
    <div className="w-10 h-10 border-2 border-rose-500/30 border-t-rose-400 rounded-full animate-spin" />
  </div>
);

// AppRouter handles session_id detection synchronously
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Public pages */}
        <Route path="/diventa-partner" element={<BecomePartner />} />
        <Route path="/richiedi-pratica" element={<RichiediPratica />} />

        {/* Service Pages */}
        <Route path="/servizi/luce-gas" element={<LuceGas />} />
        <Route path="/servizi/caf" element={<ServiziCAF />} />
        <Route path="/servizi/assicurazioni" element={<Assicurazioni />} />
        <Route path="/servizi/finanziamenti" element={<Finanziamenti />} />
        <Route path="/servizi/property-finder" element={<PropertyFinder />} />

        {/* Hub Immobiliare (Closed Beta) - solo Super Admin / Admin Sede / Operatore */}
        {/* Bypass auth se ?demo=1 (preview UI mock data, utile per share design) */}
        <Route
          path="/immobiliare"
          element={
            window.location.search.includes('demo=1') ? (
              <ImmobiliareShowcase />
            ) : (
              <ProtectedRoute allowedRoles={['super_admin', 'admin_sede', 'operatore']}>
                <ImmobiliareShowcase />
              </ProtectedRoute>
            )
          }
        />

        {/* Public document upload page */}
        <Route
          path="/upload/:praticaId"
          element={
            <ProtectedRoute>
              <DocumentUpload />
            </ProtectedRoute>
          }
        />

        {/* DASHBOARD - Area Clienti Finali */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['cliente', 'cliente_sede']}>
              <ClienteDashboard />
            </ProtectedRoute>
          }
        />

        {/* Redirect vecchio /cliente a /dashboard */}
        <Route path="/cliente" element={<Navigate to="/dashboard" replace />} />

        {/* Book Appointment - accessible to all */}
        <Route path="/prenota" element={<BookAppointment />} />

        {/* CRM - Area Agenzie, Agenti e Super Admin */}
        <Route
          path="/crm"
          element={
            <ProtectedRoute allowedRoles={['admin_sede', 'super_admin', 'operatore']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Comparatore Bollette AI - Super Admin / Admin Sede / Operatore */}
        <Route
          path="/crm/comparatore-bollette"
          element={
            <ProtectedRoute allowedRoles={['admin_sede', 'super_admin', 'operatore']}>
              <ComparatoreBollette />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <ThemeProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </ThemeProvider>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
